import { Link } from "@tanstack/react-router"
import { useQueryState, parseAsStringLiteral } from "nuqs"
import { Tabs, TabsContent, TabsList, TabsTrigger, Button } from "@aix/ui"
import CoachListTab from "./CoachListTab"
import CheckInScreen from "./CheckInScreen"
import DebugTab from "./DebugTab"
import CoachTemplatesTab from "./CoachTemplatesTab"
import WhatsNextCard from "./components/WhatsNextCard"
import ThisWeeksProgressGrid from "./components/ThisWeeksProgressGrid"
import CoachMessagesInbox from "./components/CoachMessagesInbox"
import WeeklyReviewSection from "./components/WeeklyReviewSection"

type TabValue = "dashboard" | "list" | "check-in" | "templates" | "debug"

export default function CoachOSScreen() {
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(["dashboard", "list", "check-in", "templates", "debug"]).withDefault("dashboard"),
  )

  const handleTabChange = (value: string) => {
    void setActiveTab(value as TabValue, { history: "push" })
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b-4 border-border bg-secondary p-4 md:p-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-3xl font-bold text-primary">🏃 CoachOS</h1>
          <Link
            to="/trackables"
            className="text-sm text-muted-foreground hover:text-primary transition-colors border border-border px-2 py-1 rounded hover:border-primary"
          >
            → Trackables
          </Link>
        </div>
        <p className="text-xs md:text-sm text-muted-foreground mt-2">
          Your personal coaches - pillar habits & side quests
        </p>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="border-b-2 border-border bg-background px-2 md:px-6 overflow-x-auto">
            <TabsList className="bg-transparent flex-nowrap w-full justify-start">
              <TabsTrigger
                value="dashboard"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"
              >
                <span className="mr-1">🏠</span> Dashboard
              </TabsTrigger>
              <TabsTrigger
                value="list"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"
              >
                <span className="mr-1">👥</span> Coaches
              </TabsTrigger>
              <TabsTrigger
                value="check-in"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"
              >
                <span className="mr-1">📝</span> Check-in
              </TabsTrigger>
              <TabsTrigger
                value="templates"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"
              >
                <span className="mr-1">📦</span> Templates
              </TabsTrigger>
              <TabsTrigger
                value="debug"
                className="border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"
              >
                <span className="mr-1">🐛</span> Debug
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Unified Dashboard */}
            <TabsContent value="dashboard" className="h-full m-0">
              <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-8">
                {/* Section 1: What's Next */}
                <WhatsNextCard />

                {/* Section 2: This Week's Progress */}
                <ThisWeeksProgressGrid />

                {/* Section 3: Coach Messages */}
                <CoachMessagesInbox />

                {/* Section 4: Weekly Review */}
                <WeeklyReviewSection />

                {/* Section 5: Quick Actions Footer */}
                <div className="border-t-2 border-border pt-6">
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button onClick={() => handleTabChange("templates")} variant="outline" size="sm">
                      📦 Browse Templates
                    </Button>
                    <Button onClick={() => handleTabChange("list")} variant="outline" size="sm">
                      👥 View All Coaches
                    </Button>
                    <Button onClick={() => handleTabChange("check-in")} variant="outline" size="sm">
                      📝 Quick Check-in
                    </Button>
                    <Button onClick={() => handleTabChange("debug")} variant="outline" size="sm">
                      🐛 Debug Info
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="list" className="h-full m-0">
              <CoachListTab />
            </TabsContent>
            <TabsContent value="check-in" className="h-full m-0">
              <CheckInScreen />
            </TabsContent>
            <TabsContent value="templates" className="h-full m-0">
              <CoachTemplatesTab />
            </TabsContent>
            <TabsContent value="debug" className="h-full m-0">
              <DebugTab />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}
